import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Section, SectionHeader } from "@roqua/components/sections";
import { Timeline } from "@roqua/components/Timeline";

import { BackButton } from "@/components/BackButton";
import { ErrorPage } from "@/components/ErrorPage";
import { QuestionnaireItemPreviewBlock } from "@/components/Outcomes/QuestionnaireItemPreviewBlock";
import { graphql } from "@/gql";
import { useVerifiedSession } from "@/session";
import { toError } from "@/lib/utils";
import { LoadingSpinner } from "@/components/loading";
import { Pagination } from "@/components/Pagination";
import { CursorQuery, useCursor } from "@/hooks/useCursor";

const CompletedResponsesForCurrentRespondent = graphql(/* GraphQL */ `
  query getCompletedResponsesForCurrentRespondent(
    $respondentId: ID!
    $first: Int
    $last: Int
    $before: String
    $after: String
  ) {
    respondent(id: $respondentId) {
      id
      responses(
        completed: true
        orderDescending: true
        first: $first
        last: $last
        before: $before
        after: $after
      ) {
        totalCount
        nodes {
          id
          completedAt
          questionnaire {
            name
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`);

export const Route = createFileRoute("/portal/outcomes/")({
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorPage message={toError(error).message} />,
});

function RouteComponent() {
  const { limit, cursor, setCursor } = useCursor(50);
  const { client, user } = useVerifiedSession();
  const fetchQuery = ({ first, last, before, after }: CursorQuery) => {
    return client.request(CompletedResponsesForCurrentRespondent, {
      respondentId: user.id,
      first: first,
      last: last,
      before: before,
      after: after,
    });
  };

  const { data, isFetching } = useQuery({
    queryKey: [
      "outcomes",
      cursor.first,
      cursor.last,
      cursor.before,
      cursor.after,
    ],
    queryFn: () =>
      fetchQuery({
        ...cursor
      }),
    placeholderData: keepPreviousData,
    throwOnError: true,
  });

  const respondent = data?.respondent;

  if (!respondent) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Section variant="header">
        <SectionHeader header="Uitkomsten: Vragenlijsten" buttons={<BackButton />} />
      </Section>
      <Section variant="content">
        <div className="flex flex-row gap-4 basis-1/5 justify-end">
          <Pagination
            limit={limit}
            pageInfo={respondent.responses.pageInfo}
            updateCursor={(queryData: CursorQuery) => setCursor(queryData)}
            isFetching={isFetching}
          />
        </div>
        <Timeline>
          <div className="space-y-4">
            {respondent.responses.nodes.map((response) => (
              <QuestionnaireItemPreviewBlock
                key={response.id}
                responseId={response.id}
                title={response.questionnaire.name}
                completedAt={response.completedAt}
              />
            ))}
          </div>
        </Timeline>
        <div className="flex flex-row gap-4 basis-1/5 justify-end">
          <Pagination
            limit={limit}
            pageInfo={respondent.responses.pageInfo}
            updateCursor={(queryData: CursorQuery) => setCursor(queryData)}
            isFetching={isFetching}
          />
        </div>
      </Section>
    </>
  );
}
